import {
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type MouseEvent,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useId,
} from "react";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "tinted"
  | "ghost"
  | "plain"
  | "destructive"
  | "whatsapp";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** primary (one per view), secondary, tinted (accent tint), ghost (accent text), plain
   * (neutral text), destructive, whatsapp. */
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  icon?: ReactNode;
  /** Shows a spinner, keeps the width, blocks clicks and sets aria-busy. */
  loading?: boolean;
}

/** Class list of a button, for links styled as buttons (`<Link className={buttonClass()}>`). */
export function buttonClass(
  variant: ButtonVariant = "primary",
  { size = "md", block = false, iconOnly = false }: { size?: ButtonSize; block?: boolean; iconOnly?: boolean } = {},
): string {
  return [
    "ao-button",
    `ao-button--${variant}`,
    size !== "md" && `ao-button--${size}`,
    block && "ao-button--block",
    iconOnly && "ao-button--icon",
  ]
    .filter(Boolean)
    .join(" ");
}

export function Button({
  variant = "primary",
  size = "md",
  block = false,
  icon,
  loading = false,
  type = "button",
  className = "",
  disabled,
  onClick,
  children,
  ...rest
}: ButtonProps) {
  const classes = [buttonClass(variant, { size, block }), className].filter(Boolean).join(" ");
  return (
    <button
      type={type}
      className={classes}
      disabled={disabled}
      aria-busy={loading || undefined}
      onClick={loading ? undefined : onClick}
      {...rest}
    >
      {loading ? (
        <>
          <span className="ao-button__label">
            {icon}
            {children}
          </span>
          <span className="ao-spinner" aria-hidden="true" />
        </>
      ) : (
        <>
          {icon}
          {children}
        </>
      )}
    </button>
  );
}

export interface IconButtonProps extends Omit<ButtonProps, "icon" | "children" | "block"> {
  /** Required: the accessible name (also shown as the native tooltip). */
  label: string;
  children: ReactNode;
}

/** Square button that only shows an icon (toolbars, table rows). Defaults to the plain variant. */
export function IconButton({ label, variant = "plain", size = "md", className = "", children, ...rest }: IconButtonProps) {
  return (
    <Button
      variant={variant}
      size={size}
      aria-label={label}
      title={label}
      className={`ao-button--icon ${className}`.trim()}
      {...rest}
    >
      {children}
    </Button>
  );
}

export interface SoftButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required: the button only shows an icon. */
  label: string;
}

/** Round soft-relief button (icon only). */
export function SoftButton({ label, className = "", children, ...rest }: SoftButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={`ao-soft ao-soft-button ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

export interface SoftToggleProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

export function SoftToggle({ label, checked, onChange, disabled = false }: SoftToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      className="ao-toggle"
      onClick={() => onChange(!checked)}
    >
      <span className="ao-toggle__track" aria-hidden="true">
        <span className="ao-toggle__thumb" />
      </span>
      {label}
    </button>
  );
}

/** Apple-style switch (same component as SoftToggle, clearer name for new screens). */
export const Switch = SoftToggle;
export type SwitchProps = SoftToggleProps;

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label: ReactNode;
  hint?: ReactNode;
  /** "radio" for one choice in a group (give the same `name`). */
  kind?: "checkbox" | "radio";
}

/** Checkbox or radio with its label (the whole row is clickable). */
export function Checkbox({ label, hint, kind = "checkbox", className = "", ...input }: CheckboxProps) {
  return (
    <label className={`ao-check ${className}`.trim()}>
      <input type={kind} {...input} />
      <span>
        {label}
        {hint ? <span className="ao-check__hint">{hint}</span> : null}
      </span>
    </label>
  );
}

export interface SoftSliderProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  label: string;
  value: number;
  onChange: (value: number) => void;
  /** Text shown next to the label, e.g. « 1 500 000 FCFA ». */
  valueText?: string;
}

export function SoftSlider({ label, value, onChange, valueText, ...rest }: SoftSliderProps) {
  const id = useId();
  return (
    <div className="ao-slider">
      <label htmlFor={id}>
        {label}
        {valueText ? ` — ${valueText}` : ""}
      </label>
      <input
        id={id}
        type="range"
        value={value}
        aria-valuetext={valueText}
        onChange={(event) => onChange(Number(event.target.value))}
        {...rest}
      />
    </div>
  );
}

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

export interface SegmentedControlProps<T extends string> {
  label: string;
  value: T;
  options: SegmentedOption<T>[];
  /** `origin` is the centre of the clicked option (used by the circular theme reveal). */
  onChange: (value: T, origin: { x: number; y: number }) => void;
  /** Stretch to the full width (options share it equally). */
  block?: boolean;
}

export function SegmentedControl<T extends string>({
  label,
  value,
  options,
  onChange,
  block = false,
}: SegmentedControlProps<T>) {
  const handle = (option: T) => (event: MouseEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    onChange(option, { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
  };
  return (
    <div role="radiogroup" aria-label={label} className={block ? "ao-segmented ao-segmented--block" : "ao-segmented"}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          className="ao-segmented__item"
          onClick={handle(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

interface FieldControl {
  id: string;
  "aria-invalid": true | undefined;
  "aria-describedby": string | undefined;
}

interface FieldShellProps {
  label: string;
  hint?: string;
  error?: string;
  className?: string;
  children: (control: FieldControl) => ReactNode;
}

/** Label, hint and error shared by every form field (one accessible pattern). */
function FieldShell({ label, hint, error, className = "", children }: FieldShellProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div className={`ao-field ${className}`}>
      <label htmlFor={id} className="ao-field__label">
        {label}
      </label>
      {children({
        id,
        "aria-invalid": error ? true : undefined,
        "aria-describedby": hint || error ? hintId : undefined,
      })}
      {error ? (
        <p id={hintId} role="alert" className="ao-field__error">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="ao-field__hint">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string;
}

export function TextField({ label, hint, error, className, ...input }: TextFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} className={className}>
      {(control) => <input className="ao-field__input" {...control} {...input} />}
    </FieldShell>
  );
}

export interface TextAreaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  hint?: string;
  error?: string;
}

export function TextAreaField({ label, hint, error, className, rows = 4, ...area }: TextAreaFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} className={className}>
      {(control) => (
        <textarea className="ao-field__input ao-field__input--area" rows={rows} {...control} {...area} />
      )}
    </FieldShell>
  );
}

export interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  hint?: string;
  error?: string;
  options: { value: string; label: string }[];
}

export function SelectField({ label, hint, error, className, options, ...select }: SelectFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} className={className}>
      {(control) => (
        <select className="ao-field__input" {...control} {...select}>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </FieldShell>
  );
}
